import { clsx } from 'clsx';
import { ReactNode, useCallback, useRef, useState } from 'react';

import { useDismissOnOutside } from '../hooks/useDismissOnOutside';
import styles from './Dropdown.module.scss';

/** Single row inside a `<Dropdown>` menu. */
export interface DropdownItem<T> {
  /** Value sent to `onChange` when the row is selected. */
  value: T;
  /** Plain-text label rendered in the row. */
  label: string;
  /** When true, the row is greyed-out and cannot be selected. */
  disabled?: boolean;
  /** When true, draws a divider above this row (group separator). */
  separated?: boolean;
}

/**
 * Props for `<Dropdown>`.
 *
 * Required: `items`, `selected`, `onChange`, `renderTrigger`. The trigger is
 * fully consumer-rendered (e.g. a `<Chip>` with a chevron); the dropdown
 * primitive only owns open / close state, outside-click, and Escape.
 */
export interface DropdownProps<T> {
  /** Menu items. Order is preserved. */
  items: DropdownItem<T>[];
  /** Currently selected value. The matching row gets a checked indicator. */
  selected: T;
  /** Called with the new value when a non-disabled row is clicked. */
  onChange: (value: T) => void;
  /**
   * Render the trigger element (e.g. a `<Chip>`). Receives the open state,
   * a toggle helper, and the selected item's label as a convenience.
   */
  renderTrigger: (opts: {
    open: boolean;
    toggle: () => void;
    selectedLabel: string;
  }) => ReactNode;
  /** Disables the trigger; clicking it is a no-op. */
  disabled?: boolean;
  /** Extra class name forwarded to the dropdown root element. */
  className?: string;
  /** Minimum width (px) applied to the menu surface. */
  menuMinWidth?: number;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Generic single-select dropdown used inside chartv2 (interval picker,
 * legend more-menu, etc.). The trigger is fully consumer-rendered via
 * `renderTrigger` — pass any element (typically a `<Chip>`) and wire
 * its click to the supplied `toggle` helper.
 *
 * The component owns only open/close state, outside-click, and Escape
 * handling. Items can be `disabled` or `separated` (renders a divider
 * above the row) for grouped menus.
 */
export const Dropdown = <T extends string | number>({
  items,
  selected,
  onChange,
  renderTrigger,
  disabled,
  className,
  menuMinWidth,
  'data-testid': testId,
}: DropdownProps<T>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const selectedItem = items.find(i => i.value === selected);
  const selectedLabel = selectedItem ? selectedItem.label : '';

  const toggle = useCallback(() => {
    if (disabled) {
      return;
    }

    setOpen(v => !v);
  }, [disabled]);

  useDismissOnOutside({
    open,
    refs: [rootRef],
    onDismiss: () => setOpen(false),
  });

  return (
    <div
      className={clsx(styles.root, className)}
      ref={rootRef}
      data-testid={testId}
    >
      {renderTrigger({ open, toggle, selectedLabel })}
      {open && (
        <div
          className={styles.menu}
          role="listbox"
          style={menuMinWidth ? { minWidth: menuMinWidth } : undefined}
        >
          {items.map(item => (
            <button
              key={String(item.value)}
              type="button"
              className={clsx(
                styles.item,
                item.value === selected && styles.active,
                item.disabled && styles.disabled,
                item.separated && styles.separated
              )}
              onClick={() => {
                if (item.disabled) {
                  return;
                }

                onChange(item.value);
                setOpen(false);
              }}
              disabled={item.disabled}
              role="option"
              aria-selected={item.value === selected}
              data-testid={`${testId ?? 'dropdown'}-item-${item.value}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
